import { ELEMENT_TYPES } from 'cg/components/documents/editor/slate/types'
import { Transforms, Editor, Node, Path, Range, Element as SlateElement } from 'vendor/slate'
import { ReactEditor } from 'vendor/slate-react'
import { v4 as uuidv4 } from 'uuid'

const listTypes = [ELEMENT_TYPES.unordered_list, ELEMENT_TYPES.ordered_list, ELEMENT_TYPES.check_list]
const listItemTypes = [ELEMENT_TYPES.list_item, ELEMENT_TYPES.check_list_item]

const changeListType = (editor, match, type) => {
  const [, listPath] = match

  for (const [node, path] of Editor.nodes(editor, { at: listPath })) {
    if (listTypes.includes(node.type)) {
      const { parent } = node

      Transforms.setNodes(editor, { type, parent: { type: parent && parent.type ? parent.type : null } }, { at: path })
    }

    // If checklist item and converting to normal list need to change check list item nodes
    if (node.type === ELEMENT_TYPES.check_list_item) {
      const { parent } = node

      Transforms.setNodes(
        editor,
        { type: ELEMENT_TYPES.list_item, parent: { type: parent && parent.type ? parent.type : null } },
        { at: path }
      )
    }

    //  If list item and converting to check list need to change list item nodes
    if (node.type === ELEMENT_TYPES.list_item && type === ELEMENT_TYPES.check_list) {
      const { parent } = node

      Transforms.setNodes(
        editor,
        { type: ELEMENT_TYPES.check_list_item, parent: { type: parent && parent.type ? parent.type : null } },
        { at: path }
      )
    }
  }
}

const insertUnorderedList = (editor, path) => {
  const unorderedList = {
    type: ELEMENT_TYPES.unordered_list,
    children: [
      {
        type: ELEMENT_TYPES.list_item,
        parent: { type: ELEMENT_TYPES.unordered_list },
        children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.list_item } }]
      }
    ]
  }
  const [match] = Editor.nodes(editor, { at: path, match: n => !Editor.isEditor(n) && listTypes.includes(n.type) })

  if (match) {
    const [node] = match
    if (node.type === ELEMENT_TYPES.unordered_list) return
    // change list here to a new list type
    changeListType(editor, match, ELEMENT_TYPES.unordered_list)
  } else {
    Transforms.insertNodes(editor, unorderedList, { at: path, select: true })
    ReactEditor.focus(editor)
  }
}

const insertOrderedList = (editor, path) => {
  const orderedList = {
    type: ELEMENT_TYPES.ordered_list,
    children: [
      {
        type: ELEMENT_TYPES.list_item,
        parent: { type: ELEMENT_TYPES.ordered_list },
        children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.list_item } }]
      }
    ]
  }

  const [match] = Editor.nodes(editor, { at: path, match: n => !Editor.isEditor(n) && listTypes.includes(n.type) })

  if (match) {
    const [node] = match
    if (node.type === ELEMENT_TYPES.ordered_list) return
    // change list here to a new list type
    changeListType(editor, match, ELEMENT_TYPES.ordered_list)
  } else {
    Transforms.insertNodes(editor, orderedList, { at: path, select: true })
    ReactEditor.focus(editor)
  }
}

const insertCheckList = (editor, path) => {
  const checkList = {
    type: ELEMENT_TYPES.check_list,
    children: [
      {
        type: ELEMENT_TYPES.check_list_item,
        parent: { type: ELEMENT_TYPES.check_list },
        children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.check_list_item } }]
      }
    ]
  }

  const [match] = Editor.nodes(editor, { at: path, match: n => !Editor.isEditor(n) && listTypes.includes(n.type) })

  if (match) {
    const [node] = match
    if (node.type === ELEMENT_TYPES.check_list) return
    // change list here to a new list type
    changeListType(editor, match, ELEMENT_TYPES.check_list)
  } else {
    Transforms.insertNodes(editor, checkList, { at: path, select: true })
    ReactEditor.focus(editor)
  }
}

const decreaseListItemDepth = (editor, insertShiftTab) => {
  const { selection } = editor
  if (selection) {
    const [match] = Editor.nodes(editor, { match: n => !Editor.isEditor(n) && listItemTypes.includes(n.type) })
    if (match) {
      const [node] = match
      const { text } = node.children[0]

      Transforms.liftNodes(editor, {
        match: n => !Editor.isEditor(n) && listItemTypes.includes(n.type)
      })

      const [liftedNode] = Editor.nodes(editor, { match: n => !Editor.isEditor(n) && listItemTypes.includes(n.type) })

      if (liftedNode) {
        const liftedNodeType = liftedNode[0].type
        const liftedNodeParent = liftedNode[0].parent
        const liftedNodePath = liftedNode[1]
        if (listItemTypes.includes(liftedNodeType) && !listTypes.includes(liftedNodeParent.type)) {
          Transforms.removeNodes(editor, { at: liftedNodePath })
          Transforms.insertNodes(
            editor,
            {
              type: ELEMENT_TYPES.default,
              parent: { type: null },
              children: [
                {
                  uuid: uuidv4(),
                  text,
                  parent: { type: ELEMENT_TYPES.default }
                }
              ]
            },
            { at: liftedNodePath, select: true }
          )
          ReactEditor.focus(editor)
        }
      }
      return
    }
  }
  insertShiftTab()
}

const increaseListItemDepth = (editor, insertTab) => {
  const { selection } = editor
  if (selection) {
    const [match] = Editor.nodes(editor, { match: n => !Editor.isEditor(n) && listItemTypes.includes(n.type) })
    if (match) {
      const [node, path] = match
      const parentNode = Node.parent(editor, path)
      Transforms.wrapNodes(
        editor,
        { type: node.parent.type, parent: { type: parentNode.parent && parentNode.parent.type ? parentNode.parent.type : null } },
        {
          at: path,
          split: true
        }
      )
      return
    }
  }
  insertTab()
}
const withLists = editor => {
  const { insertBreak, deleteBackward, insertTab, insertShiftTab, deleteFragment } = editor

  editor.insertBreak = () => {
    const { selection } = editor

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n => !Editor.isEditor(n) && listItemTypes.includes(n.parent && n.parent.type)
      })

      if (match) {
        const [node] = match
        if (node) {
          const { text } = node

          // check if list item is empty
          if (text && !text.length) {
            decreaseListItemDepth(editor, insertShiftTab)
            return
          }

          Transforms.splitNodes(editor, { always: true })
          return
        }
      }
    }
    insertBreak()
  }

  editor.deleteFragment = () => {
    const { selection } = editor

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n => !Editor.isEditor(n) && listItemTypes.includes(n.parent && n.parent.type)
      })

      if (match) {
        const [start, end] = Range.edges(selection)
        if (start.path.length !== end.path.length) return
        const startPathNode = Node.parent(editor, start.path)
        const endPathNode = Node.parent(editor, end.path)

        if (startPathNode.parent && endPathNode.parent && startPathNode.parent.type !== endPathNode.parent.type) return
      }
    }
    deleteFragment()
  }

  editor.deleteBackward = unit => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n => !Editor.isEditor(n) && listItemTypes.includes(n.parent && n.parent.type)
      })

      if (match) {
        const [node] = match
        if (node) {
          const { text } = node

          // check if list item is empty
          if (text && !text.length) {
            decreaseListItemDepth(editor, insertShiftTab)
            return
          }
        }
      }
    }
    deleteBackward(unit)
  }

  editor.insertTab = () => {
    increaseListItemDepth(editor, insertTab)
  }

  editor.insertShiftTab = () => {
    decreaseListItemDepth(editor, insertShiftTab)
  }

  return editor
}

export { insertUnorderedList, insertOrderedList, insertCheckList, withLists }
